import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Baby, Users, Heart, Plus, X, ArrowRight, Loader2, LayoutDashboard, LogIn } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getFavorites } from '@/utils/marketplaceEvents';
import useHasKennel from '@/hooks/useHasKennel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

/**
 * TopNavBar — marketplace primary navigation (lock per v2 P0).
 * Pozycje: Szczenięta / Reproduktory / Hodowle / ❤️ Obserwowane / ➕ Dodaj ogłoszenie (modal)
 */
const ITEMS = [
  { href: '/katalog?type=mioty', label: 'Szczenięta', icon: Baby, matchQuery: { type: 'mioty' } },
  { href: '/katalog?type=reproduktory', label: 'Reproduktory', icon: Heart, matchQuery: { type: 'reproduktory' } },
  { href: '/katalog?type=hodowle', label: 'Hodowle', icon: Users, matchQuery: { type: 'hodowle' } },
];

// @deprecated LEGACY (JEDEN EKOSYSTEM) — modal "Dodaj ogłoszenie" wyłączony z głównego flow.
// Zachowany świadomie (może być importowany gdzie indziej). Publiczne CTA → AddListingCTA → /login | /panel.
function AddListingModal({ open, onClose }) {
  const navigate = useNavigate();
  const { user, softSignup } = useAuth();
  const isAuth = !!user;
  const { hasKennel } = useHasKennel();

  // 2-step flow: 1 = pick type, 2 = email (tylko dla niezalogowanych)
  const [step, setStep] = useState(1);
  const [pickedKey, setPickedKey] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Reset state gdy modal się zamyka
  useEffect(() => {
    if (!open) {
      setTimeout(() => { setStep(1); setPickedKey(null); setEmail(''); setErr(''); setBusy(false); }, 300);
    }
  }, [open]);

  const doNavigate = (destination) => {
    onClose();
    if (destination === 'hodowla') {
      navigate('/dodaj-hodowle');
      return;
    }
    // Dla miot/reproduktor: jeśli ma hodowlę → wizard, inaczej → dodaj-hodowle z continuation flag
    if (hasKennel === false) {
      navigate(`/dodaj-hodowle?flow=${destination}`);
      return;
    }
    navigate(destination === 'miot' ? '/wizard/miot' : '/wizard/reproduktor');
  };

  const pickTile = (key) => {
    setPickedKey(key);
    setErr('');
    if (isAuth) {
      // Logged-in → bezpośrednio do wizarda
      doNavigate(key);
      return;
    }
    // Guest → krok 2 (email)
    setStep(2);
  };

  const handleSoftSignup = async (e) => {
    e.preventDefault();
    setErr('');
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setErr('Podaj prawidłowy adres e-mail.');
      return;
    }
    setBusy(true);
    try {
      await softSignup(cleanEmail, pickedKey);
      // Nowy user → nie ma hodowli. Zawsze prowadź przez /dodaj-hodowle
      // z flow param żeby po dodaniu hodowli automatycznie przejść do wizarda.
      onClose();
      if (pickedKey === 'hodowla') {
        navigate('/dodaj-hodowle');
      } else {
        navigate(`/dodaj-hodowle?flow=${pickedKey}`);
      }
    } catch (ex) {
      const code = ex?.response?.status;
      const detail = ex?.response?.data?.detail || 'Nie udało się utworzyć konta. Spróbuj ponownie.';
      if (code === 409) {
        setErr('Ten email jest już zarejestrowany. Zaloguj się, żeby kontynuować.');
      } else {
        setErr(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  // Kolejność: Szczenięta → Hodowla → Reproduktor
  const TILES = [
    { key: 'miot', title: 'Szczenięta', subtitle: 'Dodaj miot i zbieraj zapytania',
      Icon: Baby, iconBg: 'from-emerald-500/25 to-emerald-500/5', iconColor: 'text-emerald-300', featured: true },
    { key: 'hodowla', title: 'Hodowla', subtitle: 'Profil Twojej hodowli',
      Icon: Users, iconBg: 'from-blue-500/25 to-blue-500/5', iconColor: 'text-blue-300' },
    { key: 'reproduktor', title: 'Reproduktor', subtitle: 'Dodaj reproduktora',
      Icon: Heart, iconBg: 'from-purple-500/25 to-purple-500/5', iconColor: 'text-purple-300' },
  ];

  const pickedTile = TILES.find(t => t.key === pickedKey);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[#0a0f1e] border border-white/10 text-white max-w-2xl p-0 overflow-hidden sm:rounded-2xl"
        data-testid="add-listing-modal"
      >
        {/* STEP 1 — pick type */}
        {step === 1 && (
          <div className="p-6 sm:p-8">
            <DialogTitle className="text-2xl sm:text-3xl font-black text-white text-center mb-2">
              Co chcesz dodać?
            </DialogTitle>
            <p className="text-center text-sm text-gray-400 mb-6">
              Dodaj ogłoszenie i zacznij zbierać zapytania od klientów
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TILES.map(({ key, title, subtitle, Icon, iconBg, iconColor, featured }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickTile(key)}
                  data-testid={`add-option-${key}`}
                  className={`group relative p-4 sm:p-5 rounded-2xl border text-left transition-all hover:scale-[1.02] ${
                    featured
                      ? 'bg-gradient-to-br from-emerald-500/15 to-emerald-500/[0.03] border-emerald-400/40 hover:border-emerald-400/70 shadow-lg shadow-emerald-500/5'
                      : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/8'
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${iconBg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <div className="text-sm font-bold text-white mb-1">{title}</div>
                  <div className="text-xs text-gray-400 leading-relaxed">{subtitle}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-white/70 group-hover:text-white transition">
                    Dodaj <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
                  </div>
                </button>
              ))}
            </div>

            {!isAuth && (
              <p className="text-center text-[11px] text-gray-500 mt-5">
                Bez rejestracji · Dodasz w minutę · Klienci piszą bezpośrednio do Ciebie
              </p>
            )}

            {/* Panel / Login link w stopce modala */}
            <div className="mt-5 pt-4 border-t border-white/5 text-center">
              {isAuth ? (
                <Link
                  to="/panel"
                  onClick={onClose}
                  data-testid="modal-link-panel"
                  className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  Przejdź do Panelu hodowcy →
                </Link>
              ) : (
                <p className="text-xs text-gray-500">
                  Masz już konto?{' '}
                  <Link
                    to="/login"
                    onClick={onClose}
                    data-testid="modal-link-login"
                    className="inline-flex items-center gap-1 text-white/80 hover:text-white font-semibold underline underline-offset-2"
                  >
                    <LogIn className="w-3 h-3" />
                    Zaloguj się
                  </Link>
                </p>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 — soft signup (email) — tylko dla gości */}
        {step === 2 && pickedTile && (
          <div className="p-6 sm:p-8">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-4 transition"
              data-testid="soft-signup-back"
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Wróć
            </button>

            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pickedTile.iconBg} flex items-center justify-center mb-4`}>
              <pickedTile.Icon className={`w-6 h-6 ${pickedTile.iconColor}`} />
            </div>

            <DialogTitle className="text-xl sm:text-2xl font-black text-white mb-1">
              Dodanie zajmie chwilę
            </DialogTitle>
            <p className="text-sm text-gray-400 mb-5">
              Podaj e-mail, żeby zapisać ogłoszenie. Tworzymy Ci konto w tle — bez hasła, bez weryfikacji. Możesz od razu dodać <strong className="text-white">{pickedTile.title.toLowerCase()}</strong>.
            </p>

            <form onSubmit={handleSoftSignup}>
              <label className="block text-xs text-white/60 mb-1.5">Twój e-mail</label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErr(''); }}
                placeholder="ty@twoja-hodowla.pl"
                data-testid="soft-signup-email"
                className="w-full bg-white/5 border border-white/10 focus:border-emerald-400/50 focus:bg-white/10 outline-none text-white placeholder:text-white/30 rounded-xl px-4 py-3 text-sm transition"
              />

              {err && (
                <div className="mt-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="soft-signup-submit"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black font-bold py-3 transition shadow-lg shadow-orange-500/20 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Kontynuuj <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <p className="text-[10px] text-center text-white/30 mt-4">
              Rejestrując się akceptujesz{' '}
              <a href="/regulamin" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white/60">regulamin</a>{' '}
              i{' '}
              <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white/60">politykę prywatności</a>.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          data-testid="add-listing-modal-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
          aria-label="Zamknij"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}

// @deprecated LEGACY (JEDEN EKOSYSTEM) — stary dropdown z modalem. Nie używać w głównym flow.
function AddListingDropdown() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('apisai:open-add-modal', handler);
    return () => window.removeEventListener('apisai:open-add-modal', handler);
  }, []);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="top-nav-add-listing-legacy"
        className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black text-xs md:text-sm font-bold transition shadow-lg shadow-orange-500/20 shrink-0"
      >
        <Plus className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Dodaj ogłoszenie</span>
        <span className="sm:hidden">Dodaj</span>
      </button>
      <AddListingModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// JEDEN EKOSYSTEM: brak publicznych formularzy. Cały ruch tworzący prowadzi do Panelu APISAI.
// Gość → /login, zalogowany → /panel (Growth OS, gdzie używa właściwych kreatorów).
function AddListingCTA() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <button
      type="button"
      onClick={() => navigate(user ? '/panel' : '/login')}
      data-testid="top-nav-add-listing"
      className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black text-xs md:text-sm font-bold transition shadow-lg shadow-orange-500/20 shrink-0"
    >
      <Plus className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Dodaj ogłoszenie</span>
      <span className="sm:hidden">Dodaj</span>
    </button>
  );
}

export default function TopNavBar() {
  const location = useLocation();
  const currentPath = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const currentType = searchParams.get('type');
  const [favCount, setFavCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    getFavorites().then((items) => {
      if (alive) setFavCount((items || []).length);
    }).catch(() => {});
    const onChange = (e) => {
      if (e?.detail?.count !== undefined) setFavCount(e.detail.count);
    };
    window.addEventListener('apisai:favorites-changed', onChange);
    return () => {
      alive = false;
      window.removeEventListener('apisai:favorites-changed', onChange);
    };
  }, []);

  const isActive = (item) => {
    if (item.matchQuery) {
      return currentPath === '/katalog' && currentType === item.matchQuery.type;
    }
    return currentPath === item.href;
  };

  return (
    <nav
      className="w-full bg-black/60 backdrop-blur-xl border-b border-white/5"
      data-testid="top-nav-bar"
      aria-label="Nawigacja główna marketplace"
    >
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-2.5 flex items-center justify-between gap-2 md:gap-3">
        <Link to="/katalog" className="flex items-center gap-2 shrink-0" data-testid="top-nav-logo" aria-label="Wróć do katalogu">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center font-bold text-white text-sm">A</div>
          <span className="text-base font-bold text-white hidden sm:inline">ApisAI</span>
        </Link>

        <div className="flex items-center gap-1 md:gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0 justify-start md:justify-center">
          {ITEMS.map(item => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.label}
                to={item.href}
                data-testid={`top-nav-${item.label.toLowerCase()}`}
                className={`inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 rounded-full text-[11px] md:text-sm whitespace-nowrap transition-all shrink-0 ${
                  active
                    ? 'bg-white text-black font-semibold shadow'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/5'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/moje/obserwowane"
            data-testid="top-nav-favorites"
            className="relative hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/5 border border-white/10 text-pink-300 hover:bg-pink-500/15 hover:border-pink-400/40 transition"
            aria-label={`Obserwowane${favCount > 0 ? ` (${favCount})` : ''}`}
            title="Obserwowane"
          >
            <Heart className={`w-4 h-4 ${favCount > 0 ? 'fill-pink-400' : ''}`} />
            {favCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0a0f1e]"
                data-testid="top-nav-favorites-badge"
              >
                {favCount > 99 ? '99+' : favCount}
              </span>
            )}
          </Link>

          {/* Panel hodowcy (zalogowany) / Zaloguj się (gość) */}
          {user ? (
            <Link
              to="/panel"
              data-testid="top-nav-panel"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white text-xs md:text-sm font-semibold hover:bg-white/10 hover:border-white/20 transition"
              title="Panel hodowcy"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Panel hodowcy</span>
              <span className="lg:hidden">Panel</span>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="top-nav-login"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white text-xs md:text-sm font-semibold hover:bg-white/10 hover:border-white/20 transition"
              title="Zaloguj się"
            >
              <LogIn className="w-3.5 h-3.5" />
              Zaloguj się
            </Link>
          )}

          <AddListingCTA />
        </div>
      </div>
    </nav>
  );
}
